import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import { Category, ConstraintViolation, EventItem } from '../../core/types.ts';
import { CalendarView } from '../components/CalendarView.tsx';

interface CalendarPageProps {
  events: EventItem[];
  violationsMap: Map<string, ConstraintViolation[]>;
  onAddEvent: () => void;
  onEditEvent: (event: EventItem) => void;
  onDeleteEvent: (id: string) => void;
  onRescheduleEvent: (eventId: string, newDateStr: string) => void;
}

const CATEGORY_COLORS: Record<Category, string> = {
  Culture: 'var(--cat-culture)',
  Drinking: 'var(--cat-drinking)',
  Sports: 'var(--cat-sports)',
  Social: 'var(--cat-social)',
  Academic: 'var(--cat-academic)',
  Trip: 'var(--cat-trip)',
  Art: 'var(--cat-art)',
  Other: 'var(--cat-other)',
};

const CATEGORIES: Category[] = [
  'Culture',
  'Drinking',
  'Sports',
  'Social',
  'Academic',
  'Trip',
  'Art',
  'Other',
];

export const CalendarPage: React.FC<CalendarPageProps> = ({
  events,
  violationsMap,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  onRescheduleEvent,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category | 'All'>('All');

  const filteredEvents = events.filter(ev => {
    const matchesSearch =
      ev.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ev.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ev.description && ev.description.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCat = selectedCategory === 'All' || ev.category === selectedCategory;

    return matchesSearch && matchesCat;
  });

  return (
    <div className="main-container">
      {/* Clean Filter & Search Bar */}
      <div className="filter-bar">
        <div className="search-box">
          <Search size={16} strokeWidth={1.8} className="search-icon" />
          <input
            type="text"
            placeholder="Search events, venues, notes..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="icon-btn search-clear-btn"
              onClick={() => setSearchQuery('')}
              title="Clear search"
            >
              <X size={14} strokeWidth={2} />
            </button>
          )}
        </div>

        <div className="category-pills">
          <button
            className={`cat-pill ${selectedCategory === 'All' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('All')}
          >
            <span>All Events</span>
            <span className="cat-count-badge">{events.length}</span>
          </button>

          {CATEGORIES.map(cat => {
            const count = events.filter(e => e.category === cat).length;
            const dotColor = CATEGORY_COLORS[cat];
            const isSelected = selectedCategory === cat;

            return (
              <button
                key={cat}
                className={`cat-pill ${isSelected ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                <span
                  className="cat-dot"
                  style={{
                    background: dotColor,
                    boxShadow: isSelected ? `0 0 8px color-mix(in srgb, ${dotColor} 50%, transparent)` : 'none',
                  }}
                />
                <span>{cat}</span>
                <span className="cat-count-badge">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Interactive Drag and Drop Calendar View */}
      <CalendarView
        events={filteredEvents}
        violationsMap={violationsMap}
        onEditEvent={onEditEvent}
        onDeleteEvent={onDeleteEvent}
        onRescheduleEvent={onRescheduleEvent}
      />
    </div>
  );
};
